// THE SECOND SIDEBAR: the inner screens of whichever module is open.
//
// # Why a sidebar and not more tabs
//
// A module's inner surfaces were tabs — a strip under the page title, two of them on
// /leave and none anywhere else. Tabs work for two and stop working at six: the setup
// screen has five inner screens and would have needed a strip wider than its own
// heading, and the strip has nowhere to grow when a sixth arrives.
//
// A sidebar grows down, which is the axis a list of screens actually wants. It is
// ATTACHED to the icon rail rather than floating inside the page, because it is
// navigation and belongs with the other navigation — the icon rail says which module,
// this says which screen inside it, and the two read as one column with a division in
// it.
//
// # It is DECLARED, not assembled
//
// A destination carries `sections`. Adding an inner screen is one line in
// `destinations.ts` and nothing else: this renders whatever the active destination
// declares, the collapse state is the shell's, and no screen writes any of it. A module
// with no sections gets no sidebar and no toggle — the absence is silent rather than an
// empty column.
//
// # The state is the SHELL's and the control is the PAGE's
//
// The toggle sits in the page header, next to the title, because that is where somebody
// looking at content reaches to get more room for it. But a control cannot own a region
// it does not render, and the sidebar is a grid area of the shell — so the state lives
// here, in a context the shell provides, and `Page` renders a button that reads it.
// That is the whole reason this file holds a context rather than a `useState` in
// `app.tsx`.
//
// Remembered across visits, because a person who collapsed it wants it collapsed
// tomorrow. Silent on failure: private mode is not a reason to refuse.

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Link } from "react-router";
import { cn } from "@/lib/utils";
import {
  type Crumb,
  type Destination,
  entryPath,
  type RailGroup,
  type Section,
  sectionFor,
  sectionPathFor,
  trailOf,
} from "../app/destinations";
import {
  isNavigationFolder,
  type NavigationFolder,
  navigationEntries,
} from "../app/navigation-folders";
import { useT } from "../shell/i18n";
import { IconAction } from "./button";
import { moduleNavLevel, moduleNavMotion, moduleNavMotionClosing, moduleNavSpace } from "./motion";

const PREFERENCE = "riksu.module-nav";

interface ModuleNav {
  /**
   * THE MODULE IN VIEW, once, and only if the caller can reach it.
   *
   * The shell resolves this — the address through `moduleFor`, then `canReach` — and
   * everything that needs to say where you are reads it from here. It carries the whole
   * destination rather than its name because two readers need different parts of it: the
   * sidebar's heading needs the `label`, and the breadcrumb's first crumb needs the `to`
   * as well. A second resolution in either of them would be a second answer to "which
   * module is open", which is the defect `destinations.ts` exists to prevent one level up.
   *
   * The heading is not decoration. Splitting setup into five screens moved the module's
   * name out of the page title — the `h2` says "Pozisyonlar" now, where it used to say
   * "Şirketi kurma" — so without this the sidebar is five nouns with nothing saying what
   * they are five of.
   *
   * `null` means either no module or one this caller cannot reach, and the two are
   * deliberately the same answer here: a name for a place that would refuse on arrival is
   * the link-that-always-refuses defect with the link taken off.
   */
  readonly module: Destination | null;
  /**
   * THE AREA THE SIDEBAR IS DRAWING, when there is one. `null` otherwise.
   *
   * Normalised by the provider: a group that does not collapse is published as `null`, so
   * no reader has to know what `collapsed` means. Its presence is the whole question —
   * *is the sidebar an area's modules, or a module's screens*.
   *
   * TWO THINGS CAN PUT ONE HERE and the provider decides between them once: the module in
   * view sits in an area that collapses, or the reader PRESSED that area's rail entry from
   * somewhere else entirely. The second is why this is no longer a function of the address
   * alone — see `showArea`.
   */
  readonly group: RailGroup | null;
  /**
   * WHETHER THE SIDEBAR IS SHOWING THE AREA'S LIST rather than a module's screens.
   *
   * The one thing the address cannot answer, which is why it is state and not a derivation:
   * *I asked to see the area* is not a fact about where you are. Everything else about the
   * level still comes from the route — see `drilled` in `ModuleNav`.
   */
  readonly atArea: boolean;
  /**
   * SHOW AN AREA'S MODULES IN THE SIDEBAR, or stop showing one. It NAVIGATES NOWHERE.
   *
   * Two controls call it and they are the same act from two places: the rail entry that
   * names an area, and the back control at the head of a drilled-in sidebar. Neither moves
   * the reader — the breadcrumb above the page is what does that, and two controls that both
   * navigated would make one of them a worse breadcrumb.
   *
   * `null` clears it, which is what a rail entry naming a PLACE passes: pressing Leave says
   * *the sidebar is a module's screens now*, and it has to say so explicitly because pressing
   * a link to the address you are already standing on moves nothing for the reset below to
   * key on.
   */
  readonly showArea: (area: RailGroup | null) => void;
  /**
   * The inner screens of the module in view.
   *
   * Empty NO LONGER means there is no sidebar: a module inside a collapsed area draws one
   * listing the area's modules whether or not it declares screens of its own. `/onboarding`
   * is the case — it declares none and must still show the eight it sits among.
   */
  readonly sections: readonly Section[];
  /**
   * WHICH SECTION the address is, when it is one of them.
   *
   * Resolved once, beside the module, so the sidebar's highlight and the breadcrumb's
   * last crumb are the same answer rather than two lookups that agree until one of them
   * is changed.
   */
  readonly section: Section | null;
  /**
   * THE LEVELS ABOVE THE ADDRESS IN VIEW, for the page header's breadcrumb.
   *
   * Derived here from the same module the sidebar is drawn from, so "where am I" has one
   * answer for the whole shell. `Page` reads it rather than being handed crumbs by a
   * screen: a screen that could say where it is could say something else, and then the
   * trail is a per-screen list — which is the shape `destinations.ts` exists to prevent.
   */
  readonly trail: readonly Crumb[];
  /**
   * WHERE THE READER IS — the PATHNAME, and nothing derived from it.
   *
   * This used to say *"the shell resolves it (see `pageUnder` in `app.tsx`)"*, because a
   * record's address was the record's and the page had to be recovered from a hint the
   * press carried. `pageUnder` is gone: quick views now leave the pathname on the page, so
   * there is nothing left to resolve.
   *
   * **IT MUST STAY A BARE PATHNAME**, which is a constraint on the publisher rather than on
   * this file, and the reason is here because this is where the readers are: `moduleFor`
   * compares route paths; page-owned query and fragment data never belong in this value.
   *
   * Published here rather than by a second provider because "where am I" already has an
   * answer in this context and two would drift — the module, the section and the trail are
   * all derived from this same string, and a reference is a fourth reader of it.
   *
   * `""` when there is no provider, which is what a bare `render()` in a test gets: the
   * one reader outside the shell falls back to the router's own address.
   */
  readonly at: string;
  readonly open: boolean;
  readonly toggle: () => void;
}

const Context = createContext<ModuleNav>({
  module: null,
  group: null,
  atArea: false,
  showArea: () => undefined,
  sections: [],
  section: null,
  trail: [],
  at: "",
  open: true,
  toggle: () => undefined,
});

export function useModuleNav(): ModuleNav {
  return useContext(Context);
}

export interface ModuleNavProviderProps {
  /** The module in view, already narrowed to what this caller can reach. */
  readonly module: Destination | null;
  /**
   * THE AREA THAT MODULE BELONGS TO, when the area is one rail entry rather than several.
   *
   * `null` for the ordinary groups, whose members each own a rail slot — there the sidebar
   * is the module's own screens and nothing above them, exactly as before.
   *
   * PASSED rather than derived, and this is the one thing here that could not be: resolving
   * it needs `reachable`, which needs the caller's roles, and this provider has never known
   * who is asking. The shell already narrows the groups for the rail, so handing the same
   * object down costs nothing and keeps the rail and the sidebar reading ONE list — the
   * defect `destinations.ts` exists to prevent, arriving one level lower.
   */
  readonly group: RailGroup | null;
  /** The address in view. Taken as a prop rather than read from the router — see below. */
  readonly at: string;
  readonly children: ReactNode;
}

/**
 * Supplies the module in view.
 *
 * `at` is a PROP rather than a `useLocation()` in here, and that is load-bearing: the
 * page header reads this context to draw its breadcrumb, `Page` is rendered by every
 * screen, and five screens are tested with a bare `render()` outside any router. A hook
 * that needed one would turn those five green tests red for a reason that has nothing to
 * do with what they assert. The shell already reads the address to resolve the module, so
 * passing it costs nothing and keeps the router requirement where the router already is.
 */
export function ModuleNavProvider({ module, group, at, children }: ModuleNavProviderProps) {
  const [open, setOpen] = useState(() => remembered());
  const toggle = useCallback(() => {
    setOpen((was) => {
      remember(!was);
      return !was;
    });
  }, []);

  // THE AREA THE READER ASKED TO SEE, STORED BESIDE THE ADDRESS IT WAS ASKED FROM.
  //
  // It is one piece of state and it is the only thing here the route cannot answer. Pressing
  // the administration entry in the rail changes what the sidebar SHOWS and moves nobody, so
  // there is no address for the sidebar to read the level off — the reader is still standing
  // on `/leave`.
  //
  // IT EXPIRES WHEN THE ADDRESS MOVES, NOT WHEN THE ADDRESS DIFFERS, and the gap between
  // those two sentences was a reported defect: *"Press Company setup, then come back — it is
  // broken, the menu will not open again. It only happens on that one."*
  //
  // The ask used to be stored beside the address it was asked from and compared with the
  // address in view, which reads as an expiry and is not one — **an address is not a moment,
  // it is every visit to that address.** So the ask came back to life the moment the reader
  // returned to where they had asked it: press Company setup, press the area entry to come
  // back out, press Company setup again, and the comparison matches a second time. The
  // sidebar then draws the AREA while the reader is standing inside a module, and that
  // module's screens are unreachable by pressing it. *Only on that one* is the diagnosis
  // rather than a detail — the member that misbehaves is the one whose address the ask was
  // stored against, and the other seven drill in correctly on the same screen.
  //
  // NOT AN EFFECT, which is the half that was always right and is kept: an effect clearing
  // this on navigation would paint one frame of the old area over the new module. Adjusting
  // it during RENDER against the address it was last reconciled at is React's own documented
  // answer for a prop change, and it re-renders before any child sees the stale value — so
  // the property `data-table.tsx`'s paging rule is written for holds unchanged, and a rail
  // press, which moves nothing, still survives.
  const [asked, setAsked] = useState<RailGroup | null>(null);
  const [askedAt, setAskedAt] = useState(at);
  if (askedAt !== at) {
    setAskedAt(at);
    setAsked(null);
  }
  const showArea = useCallback((area: RailGroup | null) => setAsked(area), []);

  const value = useMemo(
    () => ({
      module,
      // A GROUP THAT IS NOT COLLAPSED IS NOT AN AREA, and normalising it to `null` here is
      // what keeps that judgement out of the sidebar. `collapsed` is `groupsFor`'s single
      // answer; every reader asks whether there IS a group, never whether it collapses.
      //
      // WHAT THE READER ASKED FOR WINS, and the fallback is the area the module in view sits
      // in. The two agree on every address the reader arrived at by navigating; they differ
      // exactly when somebody opened an area from outside it, which is the whole point of the
      // rail entry not being a link.
      group: asked ?? (group?.collapsed ? group : null),
      atArea: asked !== null,
      showArea,
      // DERIVED, not passed. Both of these are facts about the module, and a provider
      // taking them as separate props is a provider that can be handed a section list
      // belonging to a different module than its heading names.
      sections: module?.sections ?? [],
      section: (module ? sectionFor(module, at) : undefined) ?? null,
      trail: trailOf(module, at),
      at,
      open,
      toggle,
    }),
    [module, group, asked, showArea, at, open, toggle],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

function remembered(): boolean {
  try {
    // OPEN BY DEFAULT. A sidebar somebody has never seen is a sidebar they do not know
    // exists, and the toggle that would reveal it is an icon they have no reason to
    // press.
    return localStorage.getItem(PREFERENCE) !== "collapsed";
  } catch {
    return true;
  }
}

function remember(open: boolean): void {
  try {
    localStorage.setItem(PREFERENCE, open ? "expanded" : "collapsed");
  } catch {
    // The choice lasts for this page rather than forever, which is a smaller loss than
    // an exception thrown out of a navigation control.
  }
}

/**
 * The control that opens and closes it.
 *
 * Rendered by `Page`, beside the title. Nothing when there is no sidebar: a toggle for a
 * region that does not exist is a control that does nothing, which is worse than an absent
 * one.
 *
 * THE CONDITION IS `ModuleNav`'s OWN, and it was not — this read `sections.length === 0`
 * while the sidebar had already stopped keying on that, so `/onboarding`, `/offboarding` and
 * `/documents` drew a sidebar with NO WAY TO COLLAPSE IT. Nothing was red, because a missing
 * control looks exactly like a control somebody decided against. It is worth stating rather
 * than quietly aligning: an area's list is a sidebar like any other, and *give me more room*
 * is a preference about room whichever level is showing.
 */
export function ModuleNavToggle() {
  const t = useT();
  const { group, sections, open, toggle } = useModuleNav();

  if (sections.length === 0 && group === null) {
    return null;
  }

  return (
    <IconAction
      label={t(open ? "nav.collapseSections" : "nav.expandSections")}
      // `aria-expanded` on the control that expands something, which is the attribute
      // a screen reader reads to say whether it is open — the two icons say it to
      // everybody else.
      aria-expanded={open}
      aria-controls="module-nav"
      onClick={toggle}
      className="hidden -ml-1 lg:inline-flex"
    >
      {open ? (
        <PanelLeftCloseIcon className="size-4" aria-hidden="true" />
      ) : (
        <PanelLeftOpenIcon className="size-4" aria-hidden="true" />
      )}
    </IconAction>
  );
}

/**
 * ONE LINK IN THE SIDEBAR, whether it names a module or a screen inside one.
 *
 * Written once because the two now sit in one column and a reader has to see them as one
 * list. Declared separately they would drift into looking like two kinds of thing, which is
 * the opposite of what putting the area in this sidebar is for.
 *
 * `here` is passed rather than computed from the router's own matching, and it is a `Link`
 * rather than a `NavLink` for the same reason — which a test caught rather than a reading.
 *
 * `/payroll` is BOTH the payroll module's address and its first screen's, so the area's
 * sidebar holds two links to it. `NavLink` decides for ITSELF whether it is active and sets
 * `aria-current="page"` when it is, so both of them claimed to be the page and passing
 * `undefined` did not suppress it — an absent prop is not an override. Measured at two
 * elements where there must be one; two `aria-current="page"` announces as much as none.
 *
 * A plain `Link` cannot form an opinion, so `sectionFor`'s answer — resolved once, by the
 * provider — is the only thing that decides. That is the same rule `GroupEntry` keeps in the
 * rail one region to the left, and this file did not keep it until it was measured.
 */
function moduleNavEntry(here: boolean): string {
  return cn(
    "relative flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-shell-body no-underline",
    "hover:bg-surface-hover",
    here ? "bg-surface-selected font-medium text-foreground" : "text-muted-foreground",
  );
}

function ModuleNavLink({
  to,
  here,
  onPress,
  children,
}: {
  readonly to: string;
  readonly here: boolean;
  /**
   * WHAT THE PRESS SAYS BESIDES WHERE IT GOES, for the one caller that has something to say.
   *
   * Absent for a section link, which has nothing to add: moving to a screen of the module you
   * are already in changes nothing about the level. The area's list passes `showArea(null)` —
   * *the sidebar is this module's screens now* — because pressing the member you are already
   * standing on moves NOTHING for the address-keyed expiry to key on, which is the rail's own
   * measured rule (`IconRail`'s module arm) at the site that was missed.
   */
  readonly onPress?: () => void;
  readonly children: ReactNode;
}) {
  return (
    <Link
      to={to}
      viewTransition={!here}
      onClick={onPress}
      aria-current={here ? "page" : undefined}
      className={cn(
        // WHERE YOU ARE, SAID THREE WAYS AND NONE OF THEM AN ACCENT BAR.
        //
        // There WAS a bar here — an inset pseudo-element in `indicator.interactive` — added
        // because `surface.selected` is #E8F2F5 on the panel's #FFFFFF, **1.14:1**, and a
        // fill nobody can see is not a signal. The product owner removed it on sight:
        // *"gerek yok çizgiye, çok AI vari oluyor"*, and he is right about the artefact.
        //
        // THE MEASUREMENT THAT PUT IT THERE WAS ABOUT THE FILL ALONE, AND THE FILL WAS NEVER
        // THE ONLY SIGNAL — which is what the comment it replaced failed to notice while
        // sitting on the same line as the answer. This row already changes WEIGHT
        // (`font-medium`) and COLOUR (`text-foreground` against `text-muted-foreground`) when
        // it is the one you are on, and both are text contrast rather than the 3:1 a non-text
        // mark owes under SC 1.4.11. The quiet ground is then a third, weakest cue rather
        // than the load-bearing one.
        //
        // `aria-current="page"` is the fourth and is what a screen reader uses; it is set
        // above and is unaffected by any of this.
        moduleNavEntry(here),
      )}
    >
      {children}
    </Link>
  );
}

function ModuleNavDrill({
  onPress,
  children,
}: {
  readonly onPress: () => void;
  readonly children: ReactNode;
}) {
  return (
    <button type="button" onClick={onPress} className={moduleNavEntry(false)}>
      {children}
      <ModuleNavChevron />
    </button>
  );
}

/**
 * The one signifier for an entry that has another navigation level beneath it.
 *
 * It is decoration rather than disclosure state: pressing its row replaces one navigation
 * level with the next but does not expand a region or open a popup. The route changes only
 * after somebody chooses a leaf in that next level.
 */
function ModuleNavChevron() {
  return (
    <ChevronRightIcon
      className="ms-auto size-4 shrink-0 text-muted-foreground"
      aria-hidden="true"
    />
  );
}

/** One module inside an area's task family: a drill parent or a durable leaf. */
function AreaMember({
  item,
  here,
  onDrill,
}: {
  readonly item: Destination;
  readonly here: boolean;
  readonly onDrill: (item: Destination) => void;
}) {
  const t = useT();
  const content = <span className="min-w-0 truncate">{t(item.label)}</span>;

  return (item.sections?.length ?? 0) > 0 ? (
    <ModuleNavDrill onPress={() => onDrill(item)}>{content}</ModuleNavDrill>
  ) : (
    <ModuleNavLink to={entryPath(item)} here={here}>
      {content}
    </ModuleNavLink>
  );
}

/**
 * The sidebar. Rendered by the shell into its own grid area.
 *
 * Nothing at all when the module declares no sections. When somebody collapses it the
 * panel travels and fades where it stands, and the TRACK holding that space is released
 * only once the panel has finished leaving — see `ui/motion.ts` for why the width is not
 * what animates, and for the measurement that says why the space has to outlive the exit.
 */
export function ModuleNav() {
  const t = useT();
  // WHETHER THE READER ASKED FOR THE AREA'S LIST — the ONLY state in this drill, and it
  // lives in the provider rather than here now, because the RAIL sets it too: pressing the
  // administration entry shows the eight from wherever you are standing, and this component
  // is not where that press happens. `showArea` is the one verb; back and the rail entry are
  // two call sites of it.
  //
  // Everything else about the level is still derived from the durable route (see `drilled`
  // below). It expires when that route moves — stored beside the address it was asked from,
  // never cleared by an effect.
  const { module, group, atArea, showArea, sections, section: here, at, open } = useModuleNav();
  // THE AREA MEMBER SOMEBODY ASKED TO LOOK INSIDE, without choosing one of its screens.
  //
  // An area member with sections is a PARENT in this navigation. Following `entryPath`
  // here spent the parent press as a press on its first child — "Hiring" opened
  // "Candidates" even though Candidates had never been chosen. The nested section drill
  // below already carries the right interaction: replace this level, move no route, and
  // let a leaf press be the act that navigates. This is the same state at the level above.
  const [askedModule, setAskedModule] = useState<Destination | null>(null);
  // A TASK FAMILY BETWEEN THE AREA AND ITS MODULES. This is presentation hierarchy, not
  // module ownership: recruiting, onboarding and offboarding stay separate modules while
  // appearing together as one employee-lifecycle path.
  const [askedFolder, setAskedFolder] = useState<NavigationFolder<Destination> | null>(null);
  // A LEAF'S ROUTE STILL BELONGS TO ITS TASK FAMILY. Without this, choosing a one-screen
  // module (Leavers, Documents, Working calendar...) changed the page and snapped the
  // sidebar all the way back to Administration. The route is the durable answer after a
  // reload; this flag is only the reader explicitly backing out from that derived level.
  const [showAreaRoot, setShowAreaRoot] = useState(false);
  const [showSectionRoot, setShowSectionRoot] = useState(false);
  const [askedSection, setAskedSection] = useState<Section | null>(null);
  const [sectionRootAt, setSectionRootAt] = useState(at);
  if (sectionRootAt !== at) {
    setSectionRootAt(at);
    setAskedModule(null);
    setAskedFolder(null);
    setShowAreaRoot(false);
    setShowSectionRoot(false);
    setAskedSection(null);
  }
  const routeEntry =
    !atArea && (module?.sections?.length ?? 0) === 0 && module?.navigationFolder
      ? navigationEntries(group?.items ?? []).find(
          (entry) => isNavigationFolder(entry) && entry.key === module.navigationFolder,
        )
      : undefined;
  const routeFolder = routeEntry && isNavigationFolder(routeEntry) ? routeEntry : null;
  const activeFolder = askedFolder ?? (showAreaRoot ? null : routeFolder);
  const visibleModule = askedModule ?? (activeFolder || atArea ? null : module);
  const visibleSections = visibleModule?.sections ?? [];
  const sectionPath = visibleModule ? sectionPathFor(visibleModule, at) : undefined;
  const nestedOwner = sectionPath?.find((section) => (section.children?.length ?? 0) > 0);
  const nested = visibleModule
    ? (askedSection ?? (nestedOwner && !showSectionRoot ? nestedOwner : null))
    : null;

  // MOUNTED WHILE THERE IS SOMETHING TO LIST, open or not. Unmounting is what made this
  // appear and vanish rather than open and close.
  //
  // THE CONDITION IS NO LONGER `sections.length === 0`, and that is the line most likely to
  // be quietly wrong: `/onboarding`, `/offboarding` and `/documents` declare no sections at
  // all, so keying on the module's own screens would draw NO sidebar on three of the eight
  // administration modules — the reader would lose the area they are standing in by walking
  // into part of it. A collapsed group is itself a reason to draw one.
  if (sections.length === 0 && group === null) {
    return null;
  }

  // WHICH LEVEL, AND IT IS DERIVED FROM THE ROUTE RATHER THAN STORED.
  //
  // A cold load at `/setup/legal-entities` must land on the SECTIONS with the back control
  // armed, never on the eight — otherwise the address does not restore the screen and the
  // drill is reachable only by clicking. A stored level is exactly how the address and the
  // sidebar come to disagree, so the level is a function of the module in view: inside an
  // area, a module with screens IS level two.
  //
  // A module that declares no sections stays at level one and its entry NAVIGATES rather
  // than drilling — a drill into an empty level is a dead end with a back button on it.
  const drilled = group !== null && visibleSections.length > 0 && (askedModule !== null || !atArea);
  const inFolder = activeFolder !== null && askedModule === null;
  // THE BACK CONTROL EXISTS WHERE A LEVEL ONE WAS ACTUALLY LEFT, which is a fact about the
  // shape of the navigation rather than about the route: an ordinary module reached straight
  // off the icon rail — `/leave` — is already at its only level, and a back arrow there
  // would point at a level that does not exist.
  const label =
    nested?.label ??
    (drilled
      ? visibleModule?.label
      : inFolder
        ? activeFolder.label
        : (group?.label ?? visibleModule?.label));
  const backLabel =
    nested !== null
      ? t("nav.backToSections")
      : askedModule && activeFolder
        ? t("nav.backToParent", { name: t(activeFolder.label) })
        : t("nav.backToArea");

  const openModule = (item: Destination) => {
    setAskedModule(item);
    setShowSectionRoot(false);
    setAskedSection(null);
  };

  return (
    // THREE ELEMENTS, EACH WITH ONE JOB, and the splits are the whole fix.
    //
    // This is the grid item and it does neither of the other two jobs: it carries no width
    // of its own and nothing that transitions. It exists to be the positioning context for
    // the panel and the box that sizes the column, both of which are below.
    //
    // THE SPACE was the FIRST report — a width transition on the grid item re-laid out the
    // content column on every frame, which the product owner saw as the letters re-wrapping
    // mid-slide, a paragraph gaining a line and "stretching all the way down". Measured
    // then: 10 painted states, the table's own columns recomputed on 9 of them.
    //
    // ITS RELEASE WAS THE SECOND — the exit that went "tak diye". Collapsing on the frame
    // of the press moved the panel 224px in ONE frame at full opacity, so the eased exit
    // underneath it ran entirely off-canvas, where nobody can see an animation. The space
    // is now released only once the panel has finished leaving, and there is still EXACTLY
    // ONE re-layout because `display` does not interpolate. See `ui/motion.ts` for the
    // per-frame numbers, the two shapes that were refused, and why the box that stops being
    // rendered may not be the panel's ancestor.
    <div style={{ gridArea: "module" }} className="relative hidden lg:block">
      {/* THE SPACE, and nothing else. An empty box, in flow, and the only thing in here
          that the content column is sized by — the panel below is out of flow and
          contributes no width, which is what lets this collapse without squeezing it.
          It holds no children ON PURPOSE: this is the element that stops being rendered,
          and anything inside it would stop being rendered with it. Measured, that is not
          a nicety — with the panel underneath this box, the entrance went from 11 eased
          painted states to 2, because an element revealed out of `display: none` has no
          previous style to transition from. */}
      <div
        data-slot="module-nav-space"
        className={cn("h-full w-(--size-module-nav)", moduleNavSpace, open ? "block" : "hidden")}
      />

      <nav
        id="module-nav"
        // WHICH LIST THIS IS, and it was wrong half the time. The panel draws either a
        // module's screens or an area's modules, and both were announced as "Screens in this
        // module" — so somebody standing in Administration was told the ten entries were the
        // screens of a module they were not in. `drilled` is the same expression that decides
        // what gets rendered, so the name cannot disagree with the list. `group` is null only
        // when there are sections and no area, which is `drilled` already.
        aria-label={drilled || group === null ? t("nav.sections") : t(group.label)}
        // `inert` FROM THE PRESS, not from the end of the exit. The panel keeps its width
        // and its links whatever the track does, and the track's `display` step is now
        // DEFERRED for the length of the exit — so for those frames this is a fading
        // panel still holding real tab stops. It goes unreachable on the frame somebody
        // asks for it to, which is what they asked for; `hidden` cannot do that job here,
        // because an element that is not rendered cannot animate.
        inert={!open}
        className={cn(
          // OUT OF FLOW, which is what lets the track collapse without squeezing the
          // panel.
          //
          // ANCHORED TO THE TRACK'S RIGHT EDGE, and that is the difference between a
          // panel leaving and a rendering fault. Anchored LEFT — which is what this was
          // first — the track collapses to zero at x=56 and the panel goes on drawing
          // from there across 224px of the page that has just taken the space: watched at
          // 12x, the sidebar's own labels ghosted across the page title and the table's
          // first column for the whole fade, two sets of words in the same pixels. That
          // is the "garip" thing, arriving by a different route.
          //
          // Anchored RIGHT it is identical while open and, once the track is zero-wide,
          // it lies entirely to the LEFT of the content: 56px of it over the icon rail,
          // the other 168px off-canvas, moving further off as it travels. A panel leaving
          // past the rail it belongs to, rather than over the reader's page.
          "absolute inset-y-0 right-0 flex w-(--size-module-nav) flex-col gap-0.5",
          "overflow-x-hidden overflow-y-auto border-r border-line-subtle bg-surface-panel px-2 py-3",
          // THE BORDER AND THE PADDING ARE CONSTANT NOW. They used to toggle with the
          // width, because a zero-width sidebar still drew its right-hand rule as a
          // hairline nothing explained; the panel no longer changes width, and `opacity`
          // takes the rule with it.
          moduleNavMotion,
          open
            ? "translate-x-0 opacity-100"
            : `${moduleNavMotionClosing} -translate-x-(--motion-travel-panel) opacity-0`,
        )}
      >
        {/* WHICH MODULE THIS IS. Splitting a module into screens moves its name OUT of
          the page title — the heading says "Pozisyonlar" where it used to say "Şirketi
          kurma" — so without this the sidebar is five nouns and nothing saying what
          they are five of. It is the same word the rail's tooltip gives, from the same
          declaration, so the two cannot disagree.

          THE BREADCRUMB NOW SAYS THE SAME WORD, from the same declaration, one region
          to the right. This is left standing rather than removed, and the reason is not
          timidity: the sidebar is `lg:` only and it is collapsible, so on a narrow
          window and on a collapsed one it is the trail that carries the module's name
          and nothing here does. Whether a reader on a wide screen wants it twice is a
          design decision somebody should make with both on screen, not a deletion made
          while adding the second one. */}
        {/* THE HEADING, AND THE BACK CONTROL TO THE LEFT OF IT — which is where the product
            owner asked for it: *"Yönetim gibi nav'ın title'ının soluna da geri butonu
            koyarız, zaten breadcrumb kafası olay bu oluyor"*.

            IT DOES NOT NAVIGATE. Pressing it changes what the sidebar SHOWS and leaves the
            screen alone; the breadcrumb above the page is what moves you. Two controls that
            both navigated would make this a second, worse breadcrumb — so it is a `<button>`
            rather than a link, which is also what a screen reader needs to hear.

            The heading NAMES THE LEVEL: the module you drilled into, or the area you are
            looking at the list of. */}
        <div className="flex items-center gap-1 px-2 pt-1 pb-2">
          {nested || drilled || inFolder ? (
            <IconAction
              label={backLabel}
              onClick={() => {
                if (askedSection) setAskedSection(null);
                else if (nested) setShowSectionRoot(true);
                else if (askedModule) setAskedModule(null);
                else if (askedFolder) setAskedFolder(null);
                else if (routeFolder) setShowAreaRoot(true);
                else showArea(group);
              }}
              className="-ml-1 size-6"
            >
              <ChevronLeftIcon className="size-4" aria-hidden="true" />
            </IconAction>
          ) : null}
          {label ? (
            <h2 className="m-0 min-w-0 flex-1 truncate text-shell-caption font-medium tracking-wide text-muted-foreground uppercase">
              {t(label)}
            </h2>
          ) : null}
        </div>

        {/* THE NAVIGATION LEVELS, AND ONLY ONE IS EVER RENDERED.

            A DRILL AND NOT AN ACCORDION. The accordion shipped first and was refused: *"burayı
            drill menu yapabiliriz"*. The next level REPLACES the previous one rather than
            nesting under it, which is what keeps a deep area from becoming a column of thirty
            links. Administration may pass through a task folder before a module's screens;
            an ordinary module starts directly at its screens.

            KEYED ON THE LEVEL, so React swaps the subtree rather than reconciling one list
            into the other — without it the links would morph in place and the slide would
            play over rows that were changing their own text. */}
        <div
          key={
            nested
              ? `children:${nested.to}`
              : drilled
                ? `sections:${visibleModule?.to}`
                : inFolder
                  ? `folder:${activeFolder.key}`
                  : "root"
          }
          className={cn("flex flex-col gap-0.5", moduleNavLevel)}
        >
          {/* SCREENS when we are drilled in, OR when there is no area above this module at
              all — `/leave` off the icon rail is already at its only level, which is why it
              gets no back control. One condition rather than two blocks: an ordinary module
              and a drilled-into one draw the same thing for the same reason. */}
          {nested
            ? nested.children?.map((section) => (
                <ModuleNavLink key={section.to} to={section.to} here={section.to === here?.to}>
                  {t(section.label)}
                </ModuleNavLink>
              ))
            : drilled || group === null
              ? visibleSections.map((section) =>
                  (section.children?.length ?? 0) > 0 ? (
                    <ModuleNavDrill
                      key={section.to}
                      onPress={() => {
                        setShowSectionRoot(false);
                        setAskedSection(section);
                      }}
                    >
                      <span className="min-w-0 truncate">{t(section.label)}</span>
                    </ModuleNavDrill>
                  ) : (
                    <ModuleNavLink
                      key={section.to}
                      to={section.to}
                      here={sectionPath?.[0] === section}
                    >
                      <span className="min-w-0 truncate">{t(section.label)}</span>
                    </ModuleNavLink>
                  ),
                )
              : inFolder
                ? activeFolder.items.map((item) => (
                    <AreaMember
                      key={item.to}
                      item={item}
                      here={item.to === module?.to}
                      onDrill={openModule}
                    />
                  ))
                : // THE AREA'S TASK FAMILIES, not its internal modules as one flat list.
                  // A folder press moves no route; a direct leaf or a module's own leaf is
                  // the first control in this hierarchy that may navigate.
                  navigationEntries(group.items).map((entry) =>
                    isNavigationFolder(entry) ? (
                      <ModuleNavDrill key={entry.key} onPress={() => setAskedFolder(entry)}>
                        <span className="min-w-0 flex-1 truncate">{t(entry.label)}</span>
                      </ModuleNavDrill>
                    ) : (
                      <AreaMember
                        key={entry.to}
                        item={entry}
                        here={entry.to === module?.to}
                        onDrill={openModule}
                      />
                    ),
                  )}
        </div>
      </nav>
    </div>
  );
}
